import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import styles from "./LibraryLiteCutoff.module.css";
import SearchBar from "../../../Components/SearchBar/SearchBar";
import Paging from "../../../Components/Utils/Paging/Paging";
import ConfirmAction from "../../../Components/Utils/ConfirmAction/ConfirmAction";
import {
  getLibraryLiteCutoffs,
  updateLibraryLiteCutoff,
} from "../../../API/LibraryLite/LibraryLite";
import {
  LibraryLiteClientCutoffDto,
  LibraryLiteCutoffListDto,
} from "../../../Types/LibraryLite/LibraryLiteCutoff";

const PAGE_SIZE = 25;
const SEARCH_DELAY_MS = 300;

type PendingChange = {
  client: LibraryLiteClientCutoffDto;
  cutoffDate: string | null;
};

type EditState = {
  client: LibraryLiteClientCutoffDto;
  value: string;
};

const todayIso = (): string => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
};

const parseIso = (value: string): Date => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const formatDate = (value: string): string =>
  parseIso(value).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const daysUntil = (value: string): number =>
  Math.round((parseIso(value).getTime() - parseIso(todayIso()).getTime()) / 86_400_000);

const cutoffMeta = (client: LibraryLiteClientCutoffDto): string => {
  if (!client.cutoffDate) return "No cutoff set · Evidence tab works as normal";
  if (client.pastCutoff) {
    return `Retired ${formatDate(client.cutoffDate)} · Evidence tab is read-only`;
  }
  const days = daysUntil(client.cutoffDate);
  return `Cutoff ${formatDate(client.cutoffDate)} · in ${days} day${days === 1 ? "" : "s"}`;
};

const statusBadge = (client: LibraryLiteClientCutoffDto) => {
  if (!client.cutoffDate) return { label: "NONE", className: styles.statusNone };
  if (client.pastCutoff) return { label: "RETIRED", className: styles.statusPast };
  return { label: "SET", className: styles.statusScheduled };
};

const LibraryLiteCutoff = () => {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<LibraryLiteCutoffListDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [pending, setPending] = useState<PendingChange | null>(null);
  const requestId = useRef(0);

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search.trim()), SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    const current = ++requestId.current;
    setLoading(true);
    try {
      const result = await getLibraryLiteCutoffs({
        q: query || undefined,
        page,
        pageSize: PAGE_SIZE,
      });
      if (current === requestId.current) setData(result);
    } catch {
      if (current === requestId.current) toast.error("Failed to load client cutoffs");
    } finally {
      if (current === requestId.current) setLoading(false);
    }
  }, [query, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const clients = data?.clients ?? [];
  const cleanupEnabled = data?.cleanupEnabled ?? false;
  const pageCount = data?.totalCount ? Math.ceil(data.totalCount / PAGE_SIZE) : 0;
  const today = todayIso();

  const editValid =
    !!edit && /^\d{4}-\d{2}-\d{2}$/.test(edit.value) && edit.value !== edit.client.cutoffDate;

  const applyChange = async () => {
    if (!pending) return;
    const { client, cutoffDate } = pending;
    try {
      await updateLibraryLiteCutoff(client.id, cutoffDate);
    } catch (error) {
      toast.error(`Could not update the cutoff for ${client.name}`);
      throw error;
    }
    toast.success(
      cutoffDate
        ? `Cutoff for ${client.name} set to ${formatDate(cutoffDate)}`
        : `Cutoff cleared for ${client.name}`
    );
    await load();
  };

  const pendingIsImmediate = !!pending?.cutoffDate && pending.cutoffDate <= today;
  const pendingName = pending?.client.name ?? "this client";
  const pendingDate = pending?.cutoffDate ? formatDate(pending.cutoffDate) : "";

  const confirmTitle = !pending
    ? ""
    : !pending.cutoffDate
      ? `Clear the cutoff for ${pendingName}?`
      : pending.client.cutoffDate
        ? `Change the cutoff for ${pendingName}?`
        : `Set a cutoff for ${pendingName}?`;

  const confirmMessage = !pending
    ? ""
    : pending.cutoffDate
      ? `The Evidence tab in Enquire and Evaluate activities will be retired for ${pendingName} on ${pendingDate}. Here's what will happen:`
      : `The Evidence tab will go back to working as normal for ${pendingName}. Here's what will happen:`;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.titleBlock}>
          <p className={styles.eyebrow}>Mesma Portal</p>
          <h1>Library lite cutoff</h1>
          <p className={styles.intro}>
            Choose when each client's Evidence tab (Library lite) in Gen 2 Enquire and Evaluate is
            retired. On the cutoff date, any files still in the Evidence tab are moved into the
            Library.
          </p>
        </div>
        <div className={styles.toolbar}>
          <div className={styles.searchWrap}>
            <SearchBar
              value={search}
              onChange={(value) => {
                setSearch(value);
                setPage(1);
              }}
              type=""
            />
          </div>
          <div className={styles.toolbarStats}>
            <span className={styles.statPill}>
              <strong>{data?.scheduledCount ?? 0}</strong> client
              {(data?.scheduledCount ?? 0) === 1 ? "" : "s"} with a cutoff
            </span>
            <span
              className={`${styles.statPill} ${cleanupEnabled ? styles.statPillDanger : ""}`}
              title="Controlled by the LibraryLiteCleanupEnabled app setting"
            >
              Delete originals after migration: <strong>{cleanupEnabled ? "On" : "Off"}</strong>
            </span>
          </div>
        </div>
      </header>

      <section className={styles.workspace}>
        <div className={styles.panelHeader}>
          <div>
            <h3>Clients</h3>
            <p className={styles.panelSub}>
              {loading ? "Loading…" : `${data?.totalCount ?? 0} active client${data?.totalCount === 1 ? "" : "s"}`}
            </p>
          </div>
        </div>

        {clients.length === 0 ? (
          <div className={styles.emptyState}>
            <p>{loading ? "Loading clients…" : "No clients match your search."}</p>
          </div>
        ) : (
          <ul className={styles.clientList}>
            {clients.map((client) => {
              const badge = statusBadge(client);
              return (
                <li key={client.id} className={styles.clientRow}>
                  <div className={styles.clientMain}>
                    <span className={`${styles.statusBadge} ${badge.className}`}>{badge.label}</span>
                    <span className={styles.clientBody}>
                      <span className={styles.clientTitle}>{client.name}</span>
                      <span className={styles.clientMeta}>{cutoffMeta(client)}</span>
                    </span>
                  </div>
                  <div className={styles.clientActions}>
                    <button
                      type="button"
                      className={styles.rowAction}
                      onClick={() =>
                        setEdit({
                          client,
                          value:
                            client.cutoffDate && client.cutoffDate >= today
                              ? client.cutoffDate
                              : "",
                        })
                      }
                    >
                      {client.cutoffDate ? "Change date" : "Set cutoff"}
                    </button>
                    {client.cutoffDate && (
                      <button
                        type="button"
                        className={styles.rowActionDanger}
                        onClick={() => setPending({ client, cutoffDate: null })}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {pageCount > 1 && (
          <div className={styles.pagingWrap}>
            <Paging activePage={page} pageCount={pageCount} pageChanged={setPage} />
          </div>
        )}
      </section>

      {edit && (
        <div className={styles.modalOverlay} onClick={() => setEdit(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3>{edit.client.cutoffDate ? "Change cutoff date" : "Set cutoff date"}</h3>
            <p className={styles.modalSub}>{edit.client.name}</p>
            {edit.client.cutoffDate && (
              <p className={styles.modalHint}>
                Current cutoff: {formatDate(edit.client.cutoffDate)}
                {edit.client.pastCutoff ? " (already passed)" : ""}
              </p>
            )}
            <label>
              Cutoff date
              <input
                type="date"
                min={today}
                value={edit.value}
                onChange={(e) => setEdit({ ...edit, value: e.target.value })}
              />
            </label>
            {edit.value && edit.value <= today && (
              <p className={styles.modalWarning}>
                This is today's date, so the Evidence tab will become read-only straight away and
                files will be moved to the Library tonight.
              </p>
            )}
            <div className={styles.modalActions}>
              <button type="button" className={styles.btnSecondary} onClick={() => setEdit(null)}>
                Cancel
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                disabled={!editValid}
                onClick={() => {
                  if (!editValid) return;
                  setPending({ client: edit.client, cutoffDate: edit.value });
                  setEdit(null);
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmAction
        isVisible={!!pending}
        onClose={() => setPending(null)}
        clickHandler={applyChange}
        title={confirmTitle}
        message={confirmMessage}
        confirmText={
          !pending?.cutoffDate ? "Clear cutoff" : pending.client.cutoffDate ? "Change cutoff" : "Set cutoff"
        }
        cancelText="Cancel"
        severity={pendingIsImmediate || (!!pending?.cutoffDate && cleanupEnabled) ? "danger" : "warning"}
      >
        {pending?.cutoffDate ? (
          <ul className={styles.confirmList}>
            {pendingIsImmediate ? (
              <li className={styles.confirmWarning}>
                The date is today, so this takes effect straight away.
              </li>
            ) : (
              <li>
                Until {pendingDate}, users see a banner on the Evidence tab showing the cutoff
                date, a link to the Library and a button to copy their files across. Copying
                doesn't delete anything.
              </li>
            )}
            <li>
              From {pendingDate}, the Evidence tab becomes read-only. Users can still open and
              download their files but can't upload new ones.
            </li>
            <li>
              That night at 02:30 (UTC), and every night after, any files that haven't been copied
              yet are moved into a private Library portfolio for each activity. Their links to
              questions are kept.
            </li>
            {cleanupEnabled ? (
              <li className={styles.confirmWarning}>
                Once each copy has been checked, the original Evidence tab file is permanently
                deleted.
              </li>
            ) : (
              <li>
                The original files are kept, because deleting originals after migration is
                switched off in this environment.
              </li>
            )}
            <li>
              In Develop, notes file uploads also stop on {pendingDate}. Existing Develop files are
              kept where they are for now.
            </li>
            {pending.client.pastCutoff && !pendingIsImmediate && (
              <li>
                The Evidence tab becomes editable again until the new date. Files already moved to
                the Library stay there.
              </li>
            )}
            <li>Users will see the change the next time they load Gen 2.</li>
          </ul>
        ) : pending ? (
          <ul className={styles.confirmList}>
            <li>The banner and countdown are removed from the Evidence tab.</li>
            <li>The Evidence tab can be edited again, including uploading files.</li>
            <li>The nightly job skips this client, so no more files are moved or deleted.</li>
            <li>Files already copied to the Library stay there.</li>
            {pending.client.pastCutoff && cleanupEnabled && (
              <li className={styles.confirmWarning}>
                Original files that have already been deleted can't be restored.
              </li>
            )}
          </ul>
        ) : null}
      </ConfirmAction>
    </div>
  );
};

export default LibraryLiteCutoff;
